import { FormControl, FormHelperText, TextFieldPropsSizeOverrides, Typography } from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { IDateTimeFormat } from '../../commonFunctions/CommonFunctions';

interface ControlledDateTimeFieldProps {
    label: string;
    id: string;
    name: string;
    className?: string;
    variant?: 'standard' | 'filled' | 'outlined';
    disabled?: boolean;
    control: any;
    defaultValue?: any;
    dateFormat?: IDateTimeFormat;
    rules: UseControllerProps['rules'];
    onChange?: (date: Date | null) => void;
    required?: boolean;
    setValue?: any;
    denyFuturOrPast?: 'none' | 'future' | 'past' | 'custom';
    description?: IDescription;
    dateSize?: OverridableStringUnion<'small' | 'medium', TextFieldPropsSizeOverrides>;
    viewClock?:boolean
}

interface IDescription {
    text: string;
    align: 'Top' | 'Bottom';
    style?: React.CSSProperties;
    className?: string;
}

const WSDateTimeField: React.FC<ControlledDateTimeFieldProps> = ({
    id,
    label,
    className,
    control,
    name,
    rules,
    variant = 'outlined',
    disabled = false,
    defaultValue = null,
    setValue,
    onChange,
    denyFuturOrPast,
    description,
    dateSize = 'small',
    dateFormat = 'DD-MM-YYYY HH:mm:ss',
    viewClock=true
}) => {
    useEffect(() => {
        if (setValue && defaultValue) {
            setValue(name, dayjs(defaultValue).format(dateFormat));
        }
    }, [setValue, name, defaultValue, dateFormat]);

    const maxDate = denyFuturOrPast === 'future' ? dayjs().toDate() : undefined;
    const minDate = denyFuturOrPast === 'past' ? dayjs().toDate() : undefined;

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={rules}
            render={({ field, fieldState }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <FormControl className={className} disabled={disabled} error={!!fieldState.error} fullWidth>
                        {description && description.align === 'Top' && (
                            <Typography style={description.style}>{description.text}</Typography>
                        )}
                        <DateTimePicker
                            value={field.value ? dayjs(field.value, dateFormat) : null}
                            label={label}
                            disabled={disabled}
                            format={dateFormat}
                            maxDate={maxDate ? dayjs(maxDate) : undefined}
                            minDate={minDate ? dayjs(minDate) : undefined}
                            ampm={true}
                            slotProps={{
                                textField: { size: dateSize, },digitalClockSectionItem:{sx:{fontSize:'0.75rem !important'}} ,
                                layout: {
                                    sx: {
                                    //   width: "250px", // Adjust overall width of the picker popup
                                    //   height: "300px", // Adjust height of the picker popup
                                      "& .MuiPickersLayout-actionBar": {
                                        gridRow: 2,
                                        minWidth:0,
                                        marginTop:viewClock?'18rem':'18rem',
                                        marginRight:viewClock?'20rem':'10rem',
                                  
                                      },
                                      
                                    }
                                  },
                                popper: {
                                    modifiers: [
                                        {
                                          name: 'offset',
                                          options: {
                                            offset: [0, 1],
                                          },
                                        },
                                      ],
                                    sx: {
                                        // '& .MuiPickersLayout-root': {
                                        //     maxWidth: 500, // adjust as needed
                                        //     maxHeight: 250, // prevent overflow
                                        //   },
                                        //   '& .MuiPickersCalendarHeader-root': {
                                        //     fontSize: '0.75rem',
                                        //   },
                                        //   '& .MuiPickersDay-root': {
                                        //     fontSize: '0.75rem',
                                        //     width: 30,
                                        //     height: 30,
                                        //     margin: '1px',
                                        //   },
                                        //   '& .MuiPickersTimeClock-root': {
                                        //     height: 200,
                                        //     width: 200,
                                        //   },
                                        '& .MuiPickersToolbar-root': {
                                            alignItems: 'flex-start',
                                            justifyContent: 'flex-start',
                                            paddingLeft: '12px',
                                            
                                        },
                                        '& .MuiTypography-h4': {
                                            fontSize: '0.75rem', // Adjust selected date size
                                            textAlign: 'left',
                                            
                                        },
                                       
                                    },
                                },
                            }}
                           
                            viewRenderers={viewClock ?{
                                hours: renderTimeViewClock,
                                minutes: renderTimeViewClock,
                                seconds: renderTimeViewClock,
                            }:undefined}
                            onChange={(date: any) => {
                                const formattedDate: any = date ? dayjs(date).format(dateFormat) : null;
                                field.onChange(formattedDate);
                                if (onChange) {
                                    onChange(formattedDate);
                                }
                            }}
                            sx={ 
                                fieldState.error
                                    ? {
                                          '& .MuiInputBase-root': {
                                              borderColor: 'red',
                                              borderWidth: 1,
                                              borderStyle: 'solid',
                                              fontSize: '0.75rem'
                                          },
                                      }
                                    : {
                                          '& .MuiInputBase-input': { fontSize: '0.75rem', },
                                          '& .MuiFormHelperText-root': { fontSize: '0.75rem', },
                                          '& .MuiInputLabel-root': { fontSize: '0.75rem', },
                                          '& .MuiFormLabel-root':{  fontSize: '0.75rem',},
                                         
                                      }
                            }
                        />
                        {fieldState.error && <FormHelperText sx={{fontSize: '0.75rem'}}>{fieldState.error.message}</FormHelperText>}
                        {description && description.align === 'Bottom' && (
                            <Typography style={description.style} className={description?.className}>
                                {description.text}
                            </Typography>
                        )}
                    </FormControl>
                </LocalizationProvider>
            )}
        />
    );
};

export default WSDateTimeField;
